import React from 'react';
import ReactDOM from 'react-dom/client';
import { BubbleChat } from './components/BubbleChat';
import { initializeWidget, WidgetConfig } from './lib/config';
import './styles/index.css';

// Widget configuration interface
export interface BubbleChatConfig extends Partial<WidgetConfig> {
  position?: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';
  bubbleColor?: string;
  bubbleSize?: number;
  containerId?: string;
}

// Global SmartPosAgentChat interface
interface SmartPosAgentChatWidget {
  init: (config?: BubbleChatConfig) => void;
  destroy: () => void;
  open: () => void;
  close: () => void;
}

let rootInstance: ReactDOM.Root | null = null;
let containerElement: HTMLDivElement | null = null;
let chatConfig: BubbleChatConfig = {};

// Create container for the widget
function createContainer(containerId?: string): HTMLDivElement {
  const wrapperId = containerId || 'smartposagent-chat-widget';
  let container = document.getElementById(wrapperId) as HTMLDivElement;

  if (!container) {
    // Create outer container with pointer-events: none
    container = document.createElement('div');
    container.id = wrapperId;
    container.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      pointer-events: none;
      z-index: 9999;
    `;

    // Create inner wrapper for pointer events - this is where React will mount
    const innerWrapper = document.createElement('div');
    innerWrapper.id = `${wrapperId}-inner`;
    innerWrapper.style.cssText = `
      position: relative;
      width: 100%;
      height: 100%;
      pointer-events: auto;
    `;
    container.appendChild(innerWrapper);
    document.body.appendChild(container);
  }

  // Return the inner wrapper for React mounting
  const innerWrapper = document.getElementById(`${wrapperId}-inner`) as HTMLDivElement;
  return innerWrapper || container;
}

// Widget API
const SmartPosAgentChat: SmartPosAgentChatWidget = {
  /**
   * Initialize the bubble chat widget
   * @param config - Widget configuration
   */
  init(config: BubbleChatConfig = {}) {
    chatConfig = config;

    // Initialize API config
    if (config.apiUrl || config.chatEndpoint || config.documentEndpoint) {
      initializeWidget({
        apiUrl: config.apiUrl,
        chatEndpoint: config.chatEndpoint,
        documentEndpoint: config.documentEndpoint,
        mode: config.mode || 'embedded',
      });
    }

    // Create container
    containerElement = createContainer(config.containerId);

    // Mount React app
    if (rootInstance) {
      rootInstance.unmount();
    }

    rootInstance = ReactDOM.createRoot(containerElement);
    rootInstance.render(
      <React.StrictMode>
        <BubbleChat
          position={config.position}
          bubbleColor={config.bubbleColor}
          bubbleSize={config.bubbleSize}
        />
      </React.StrictMode>
    );

    console.log('[SmartPosAgent Chat] Widget initialized', config);
  },

  /**
   * Destroy the widget
   */
  destroy() {
    if (rootInstance) {
      rootInstance.unmount();
      rootInstance = null;
    }

    if (containerElement && containerElement.parentNode) {
      containerElement.parentNode.removeChild(containerElement);
      containerElement = null;
    }

    console.log('[SmartPosAgent Chat] Widget destroyed');
  },

  /**
   * Programmatically open the chat
   */
  open() {
    // This will be implemented via event system if needed
    console.log('[SmartPosAgent Chat] Open method called');
  },

  /**
   * Programmatically close the chat
   */
  close() {
    // This will be implemented via event system if needed
    console.log('[SmartPosAgent Chat] Close method called');
  },
};

// Auto-initialize if data attributes are present
function autoInit() {
  const script = document.currentScript as HTMLScriptElement;

  if (script) {
    const config: BubbleChatConfig = {
      apiUrl: script.getAttribute('data-api-url') || undefined,
      chatEndpoint: script.getAttribute('data-chat-endpoint') || undefined,
      documentEndpoint: script.getAttribute('data-document-endpoint') || undefined,
      position: (script.getAttribute('data-position') as any) || 'bottom-right',
      bubbleColor: script.getAttribute('data-bubble-color') || '#007bff',
      bubbleSize: parseInt(script.getAttribute('data-bubble-size') || '60'),
    };

    // Auto-init if any config is provided or if data-auto-init is true
    const autoInitAttr = script.getAttribute('data-auto-init');
    if (autoInitAttr !== 'false') {
      // Wait for DOM to be ready
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => SmartPosAgentChat.init(config));
      } else {
        SmartPosAgentChat.init(config);
      }
    }
  }
}

// Expose globally
if (typeof window !== 'undefined') {
  (window as any).SmartPosAgentChat = SmartPosAgentChat;

  // Auto-initialize
  autoInit();
}

export default SmartPosAgentChat;
